import React, { useState } from 'react';
import { actions, inputs, elements, navigation, overlays } from '@ecosystem/newron-design-system';
import { Modal } from '../../design/modal/Modal';
import { DataStore } from '../../data/store';
import { BasicTrackingFn, Params } from '../../types';
import { UiDataParams } from '../../data';
import { trackPageElementClicked } from '../../analytics/utils';
import { validColumnsFilter } from './helper';
import { triggerUpsellQualtrics, UpgradeType } from '../../analytics/upsell.qualtrics';
import { ContextManager } from '../../context/manager';
import { useCorePageState } from '../../page/provider';

type KpiOption = {
  value: string;
  label: string;
  enrichment?: string;
};

type Props = {
  onClose: () => void;
  paramName: string;
  currentKpis: string[];
  data: DataStore;
  updateSettings: (updates: Params) => void;
  maxCount: number;
  onRearrange?: (event: Event) => void;
  trackEvent?: BasicTrackingFn;
};
function CustomiseKpis({
  onClose,
  onRearrange,
  paramName,
  currentKpis,
  data,
  updateSettings,
  maxCount,
  trackEvent,
}: Props) {
  const [selected, setSelected] = useState<string[]>(currentKpis);

  const [toast, setToast] = useState<{ type: overlays.Notification; message: string }>();
  const { Icon } = elements;
  const { Toast } = overlays;
  const { Link } = navigation;
  const corePageState = useCorePageState();
  const context = ContextManager.getInstance().get();
  const options: KpiOption[] = data
    .getDataColumns()
    .filter(validColumnsFilter)
    .map(col => ({
      value: col.name,
      label: col.label,
      enrichment: col.enrichment,
    }));
  options.push(
    ...data
      .getAvailableColumns()
      .filter(validColumnsFilter)
      .map(col => ({
        value: col.name,
        label: col.label,
        enrichment: col.enrichment,
      }))
  );
  const selectedOptions: KpiOption[] = [];
  selected.forEach(value => {
    const option = options.find(opt => opt.value === value);
    if (option) {
      selectedOptions.push(option);
    }
  });

  const handleRearrange = event => {
    const newArray: string[] = [];
    const newIndexOrder = event.newIndexOrder;
    newIndexOrder.forEach((index: number) => {
      newArray.push(selected[index]);
    });
    setSelected(newArray);
    onRearrange?.(event);
  };

  const handleDelete = (index: number) => {
    const newSelected = [...selected];
    newSelected.splice(index, 1);
    setSelected(newSelected);
  };

  const handleAdd = () => {
    const toAdd = options.find(opt => !selected.includes(opt.value));
    if (toAdd) {
      const newSelected = [...selected];
      newSelected.push(toAdd.value);
      setSelected(newSelected);
    }
  };

  const handleChange = (index: number, value: string) => {
    const newSelected = [...selected];
    newSelected.splice(index, 1, value);
    setSelected(newSelected);
  };

  const handleClose = () => {
    onClose();
    trackPageElementClicked(trackEvent, 'Close custom KPIs');
  };

  const handleCancel = () => {
    onClose();
    trackPageElementClicked(trackEvent, 'Cancel custom KPIs');
  };

  const handleApply = () => {
    const columns: string[] = [];
    const labels: string[] = [];
    selected.forEach(name => {
      const option = options.find(opt => opt.value === name);
      if (option) {
        columns.push(option.value);
        labels.push(option.label);
      }
    });

    // filter out any undefined enrichments
    const enrichments = selectedOptions.map(opt => opt.enrichment).filter(Boolean);
    const uniqueEnrichments = [...new Set(enrichments)];

    const updates = {
      [paramName]: columns,
      [UiDataParams.uiEnrichments]: uniqueEnrichments,
    };
    updateSettings(updates);
    onClose();
    trackPageElementClicked(trackEvent, 'Add and save custom KPIs', { [paramName]: labels });
  };

  const hasChanged = currentKpis != selected;
  const hasMaxCount = maxCount > 0;

  const { Button, DragAndDrop } = actions;
  return (
    <Modal size="sm" isOpen onClose={handleClose}>
      <Modal.Header>Customise KPIs</Modal.Header>
      <Modal.Body>
        <div className="relative" style={{ minHeight: '496px' }}>
          {hasMaxCount && <div className="font-bold mb-2">Add up to {maxCount} KPIs</div>}
          <div className="pb-6">
            <DragAndDrop onChange={handleRearrange} onKeyDown={() => {}}>
              {selectedOptions.map((opt, idx) => (
                <div
                  data-test-id={`${opt.value}_${idx}`}
                  data-testid={`${opt.value}_${idx}`}
                  key={`${opt.value}_${idx}`}
                  className="pl-8 py-1"
                >
                  <KpiEntry
                    option={opt}
                    options={options}
                    onDelete={selected.length > 1 ? () => handleDelete(idx) : undefined}
                    onChange={value => handleChange(idx, value)}
                  />
                </div>
              ))}
            </DragAndDrop>
            {(!hasMaxCount || selected.length < maxCount) && (
              <div className="mt-2">
                <Button onClick={handleAdd}>Add</Button>
              </div>
            )}
          </div>
        </div>
        {data.getUpsellKpisIndicator() && (
          <div className="border-t border-light flex p-2">
            <Icon color="default">
              <Icon.icons.LockOpenOutline />
            </Icon>
            <div className="pl-2">
              Gain access to more KPIs with a subscription{' '}
              <Link
                onClick={() => {
                  triggerUpsellQualtrics(context, setToast, corePageState, UpgradeType.KPI_UPGRADE);
                }}
              >
                upgrade
              </Link>
              {toast && (
                <Toast type={toast.type} onComplete={() => setToast(undefined)}>
                  {toast.message}
                </Toast>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button mode="secondary" onClick={handleCancel} testId="modal-cancel-btn">
          Cancel
        </Button>
        <Button disabled={!hasChanged} onClick={handleApply} testId="modal-apply-btn">
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

type EntryProps = {
  option: KpiOption;
  onDelete?: () => void;
  onChange: (name: string) => void;
  options: KpiOption[];
};
function KpiEntry({ option, options, onDelete, onChange }: EntryProps) {
  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  const handleChange = (value: string | string[] | null) => {
    if (typeof value !== 'string') {
      return;
    }
    onChange(value);
  };

  const { Select } = inputs;
  const { IconButton } = actions;
  return (
    <div className="relative flex items-center gap-2 mr-2">
      <div className="flex-grow">
        <Select options={options} value={option.value} onChange={handleChange} />
      </div>
      <IconButton testId={`${option.value}-delete-btn`} onClick={handleDelete} disabled={onDelete === undefined}>
        <IconButton.icons.RemoveOutline />
      </IconButton>
    </div>
  );
}

export { CustomiseKpis };
